import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Autocomplete,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Chip,
  Divider,
  Grid,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';

interface DocumentCategory {
  id: string;
  name: string;
  description: string;
}

interface MetadataFormData {
  category: string;
  tags: string[];
  description: string;
  department?: string;
  classification: 'public' | 'internal' | 'confidential';
  permissions: {
    defaultAccess: 'private' | 'team' | 'organization';
    allowShare: boolean;
    allowDownload: boolean;
  };
}

interface DocumentMetadataFormProps {
  onSubmit: (metadata: MetadataFormData) => void;
  fileCount: number;
}

const DocumentMetadataForm: React.FC<DocumentMetadataFormProps> = ({
  onSubmit,
  fileCount,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<MetadataFormData>({
    category: '',
    tags: [],
    description: '',
    department: '',
    classification: 'internal',
    permissions: {
      defaultAccess: 'team',
      allowShare: true,
      allowDownload: true,
    },
  });

  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // TODO: Fetch categories from API instead of hardcoding
  // These IDs must match the document_categories.code values in the database (migration 008)
  const availableCategories: DocumentCategory[] = [
    { id: 'design_standards', name: t('categories.design_standard'), description: t('categories.design_standardDesc') },
    { id: 'incident_reports', name: t('categories.incident'), description: t('categories.incidentDesc') },
    { id: 'technical_specs', name: t('categories.technical_specs'), description: t('categories.technical_specsDesc') },
    { id: 'operation_manuals', name: t('categories.operation_manuals'), description: t('categories.operation_manualsDesc') },
  ];

  const availableTags = [
    'quarterly', 'annual', 'report', 'analysis', 'strategy', 'budget',
    'presentation', 'meeting', 'proposal', 'guidelines', 'policy',
    'procedure', 'training', 'assessment', 'review', 'planning',
  ];

  const departments = [
    t('departments.finance'),
    t('departments.marketing'),
    t('departments.engineering'),
    t('departments.humanResources'),
    t('departments.sales'),
    t('departments.operations'),
    t('departments.legal'),
    t('departments.executive'),
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.category) {
      newErrors.category = t('metadata.categoryRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setFormData(prev => ({
      ...prev,
      category: event.target.value,
    }));
  };

  const handleTagsChange = (_: any, newTags: string[]) => {
    setFormData(prev => ({
      ...prev,
      tags: newTags,
    }));
  };

  const handleAddCustomTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const handlePermissionChange = (permission: keyof MetadataFormData['permissions']) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [permission]: event.target.checked,
        },
      }));
    };

  const handleAccessLevelChange = (event: SelectChangeEvent<string>) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        defaultAccess: event.target.value as 'private' | 'team' | 'organization',
      },
    }));
  };

  const handleClassificationChange = (event: SelectChangeEvent<string>) => {
    setFormData(prev => ({
      ...prev,
      classification: event.target.value as 'public' | 'internal' | 'confidential',
    }));
  };

  const handleDepartmentChange = (event: SelectChangeEvent<string>) => {
    setFormData(prev => ({
      ...prev,
      department: event.target.value,
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          {t('metadata.metadataNote', {
            count: fileCount,
            files: fileCount === 1 ? t('metadata.file') : t('metadata.files')
          })}
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('metadata.basicInformation')}
              </Typography>

              <Box display="flex" flexDirection="column" gap={3}>
                <FormControl fullWidth error={!!errors.category}>
                  <InputLabel>{t('metadata.category')} *</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={handleCategoryChange}
                    label={`${t('metadata.category')} *`}
                  >
                    {availableCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        <Box>
                          <Typography variant="body1">{category.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {category.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.category && (
                    <Typography variant="caption" color="error">
                      {errors.category}
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  label={t('metadata.description')}
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  error={!!errors.description}
                  helperText={errors.description || t('metadata.descriptionHelp')}
                  fullWidth
                />

                <FormControl fullWidth>
                  <InputLabel>{t('metadata.department')}</InputLabel>
                  <Select
                    value={formData.department || ''}
                    onChange={handleDepartmentChange}
                    label={t('metadata.department')}
                  >
                    <MenuItem value="">
                      <em>{t('metadata.none')}</em>
                    </MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept} value={dept}>
                        {dept}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tags */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('metadata.tags')}
              </Typography>

              <Autocomplete
                multiple
                value={formData.tags}
                onChange={handleTagsChange}
                options={availableTags}
                freeSolo
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} label={option} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('metadata.tags')}
                    placeholder={t('metadata.tagsPlaceholder')}
                    helperText={t('metadata.tagsHelp')}
                  />
                )}
              />

              <Box display="flex" alignItems="center" gap={2} sx={{ mt: 2 }}>
                <TextField
                  size="small"
                  label={t('metadata.addCustomTag')}
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomTag();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddCustomTag}
                  disabled={!newTag.trim()}
                >
                  {t('metadata.add')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Classification & Security */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('metadata.classificationSecurity')}
              </Typography>

              <Box display="flex" flexDirection="column" gap={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('metadata.classification')}</InputLabel>
                  <Select
                    value={formData.classification}
                    onChange={handleClassificationChange}
                    label={t('metadata.classification')}
                  >
                    <MenuItem value="public">
                      <Box>
                        <Typography variant="body1">{t('metadata.public')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('metadata.publicDesc')}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="internal">
                      <Box>
                        <Typography variant="body1">{t('metadata.internal')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('metadata.internalDesc')}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="confidential">
                      <Box>
                        <Typography variant="body1">{t('metadata.confidential')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('metadata.confidentialDesc')}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <Divider />

                <FormControl fullWidth>
                  <InputLabel>{t('metadata.defaultAccessLevel')}</InputLabel>
                  <Select
                    value={formData.permissions.defaultAccess}
                    onChange={handleAccessLevelChange}
                    label={t('metadata.defaultAccessLevel')}
                  >
                    <MenuItem value="private">{t('metadata.privateOnly')}</MenuItem>
                    <MenuItem value="team">{t('metadata.teamDepartment')}</MenuItem>
                    <MenuItem value="organization">{t('metadata.organizationEveryone')}</MenuItem>
                  </Select>
                </FormControl>

                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.permissions.allowShare}
                        onChange={handlePermissionChange('allowShare')}
                      />
                    }
                    label={t('metadata.allowShare')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.permissions.allowDownload}
                        onChange={handlePermissionChange('allowDownload')}
                      />
                    }
                    label={t('metadata.allowDownload')}
                  />
                </FormGroup>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={!formData.category}
        >
          {t('metadata.continueToUpload')}
        </Button>
      </Box>
    </form>
  );
};

export default DocumentMetadataForm;
